import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, ChevronDown, ChevronUp, Lock, Unlock } from 'lucide-react';
import { ReviewableField } from './ReviewableField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_MAP = { quote: 'quote', invoice: 'invoice', 'order confirmation': 'purchase_order', 'packing slip': 'packing_slip' };

const stateOf = (f) => {
  if (!f.reviewed) return null;
  if (f.rejected) return 'rejected';
  if (f.corrected) return 'corrected';
  return 'confirmed';
};

const FieldReviewPanel = ({ documentId, extractedData, onValueCorrected }) => {
  const [fields, setFields] = useState(extractedData.fields || []);
  const [collapseConfirmed, setCollapseConfirmed] = useState(false);
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    // re-hydrate from server so refreshes don't lose progress
    let cancelled = false;
    axios.get(`${API}/documents/${documentId}/corrections`, { withCredentials: true })
      .then((res) => {
        if (cancelled || !res.data?.events?.length) return;
        const byId = {};
        res.data.events.forEach((e) => { byId[e.field_id] = e; });
        setFields((prev) => prev.map((f) => {
          const e = byId[f.field_id];
          if (!e) return f;
          return {
            ...f,
            reviewed: true,
            corrected: e.action === 'correct',
            rejected: e.action === 'reject',
            value: e.action === 'correct' ? e.corrected_value : f.value,
          };
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [documentId]);

  const reviewedCount = useMemo(() => fields.filter((f) => f.reviewed).length, [fields]);
  const total = fields.length;
  const verified = total > 0 && reviewedCount === total;
  const locked = verified && !unlocked;

  const docTypeField = fields.find((f) => f.field_path === 'document_type');
  const vendorField = fields.find((f) => f.field_path === 'vendor_name');

  const buildEvent = useCallback((field, action, correctedValue = null) => ({
    field_id: field.field_id,
    field_label: field.field_label,
    field_path: field.field_path,
    stage: field.stage,
    source_span: String(field.source_span || field.value || '—'),
    source_offset: field.source_offset || null,
    ai_value: field.value,
    ai_confidence: field.confidence,
    action,
    corrected_value: correctedValue,
    model_version: extractedData.model_version || '',
    prompt_version: extractedData.prompt_version || '',
    doc_type: DOC_TYPE_MAP[String(docTypeField?.value || '').toLowerCase()] || null,
    vendor: vendorField?.value || null,
  }), [extractedData, docTypeField, vendorField]);

  const postEvents = useCallback(async (events, apply) => {
    const snapshot = fields;
    setFields(apply(fields));
    try {
      await axios.post(`${API}/documents/${documentId}/corrections`, { events }, { withCredentials: true });
      toast.success(events.length > 1 ? `${events.length} fields confirmed` : 'Saved');
    } catch (err) {
      setFields(snapshot);
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not save review');
    }
  }, [fields, documentId]);

  const patch = (fieldId, changes) => (list) =>
    list.map((f) => (f.field_id === fieldId ? { ...f, ...changes } : f));

  const handleConfirm = (field) =>
    postEvents([buildEvent(field, 'confirm')], patch(field.field_id, { reviewed: true, corrected: false, rejected: false }));

  const handleReject = (field) =>
    postEvents([buildEvent(field, 'reject')], patch(field.field_id, { reviewed: true, corrected: false, rejected: true }));

  const handleCorrect = (field, newValue) => {
    postEvents(
      [buildEvent(field, 'correct', newValue)],
      patch(field.field_id, { value: newValue, reviewed: true, corrected: true, rejected: false }),
    );
    if (onValueCorrected) onValueCorrected(field.field_path, newValue);
  };

  const highConfUnreviewed = fields.filter((f) => !f.reviewed && f.confidence >= 0.9);

  const handleConfirmAllHigh = async () => {
    if (highConfUnreviewed.length === 0) return;
    setBulkRunning(true);
    const events = highConfUnreviewed.map((f) => buildEvent(f, 'confirm'));
    const ids = new Set(highConfUnreviewed.map((f) => f.field_id));
    await postEvents(events, (list) =>
      list.map((f) => (ids.has(f.field_id) ? { ...f, reviewed: true, corrected: false, rejected: false } : f)),
    );
    setCollapseConfirmed(true);
    setBulkRunning(false);
  };

  const confirmedFields = fields.filter((f) => stateOf(f) === 'confirmed');
  const attentionFields = fields.filter((f) => stateOf(f) !== 'confirmed');
  const needsEyes = fields.filter((f) => !f.reviewed).length;

  return (
    <div className="mb-6 bg-white border-2 border-black shadow-[4px_4px_0_0_#111111]" data-testid="field-review-panel">
      {/* Header */}
      <div className="p-4 border-b-2 border-black flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h3 className="font-heading font-bold uppercase tracking-wide text-sm">Field Review</h3>
          {verified && (
            <span
              data-testid="verified-badge"
              className="inline-block px-3 py-1 bg-primary text-black font-heading font-black uppercase tracking-widest text-xs border-2 border-black -rotate-2 shadow-[2px_2px_0_0_#111111]"
            >
              Verified
            </span>
          )}
        </div>
        {verified && (
          <button
            onClick={() => setUnlocked(!unlocked)}
            data-testid="edit-review-toggle"
            className="h-8 px-3 text-[10px] font-heading font-bold uppercase tracking-wider border-2 border-black bg-white hover:bg-zinc-100 flex items-center gap-1.5"
          >
            {unlocked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {unlocked ? 'Lock review' : 'Edit review'}
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Progress */}
        <div data-testid="review-progress">
          <div className="flex justify-between text-[10px] font-heading font-bold uppercase tracking-wider mb-1">
            <span>Reviewed {reviewedCount} / {total}</span>
            {needsEyes > 0 && <span className="text-zinc-500">{needsEyes} field{needsEyes !== 1 ? 's' : ''} need your eyes</span>}
          </div>
          <div className="h-2.5 bg-black overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${total ? (reviewedCount / total) * 100 : 0}%` }} />
          </div>
        </div>

        {/* Bulk confirm */}
        {!locked && highConfUnreviewed.length > 0 && (
          <button
            onClick={handleConfirmAllHigh}
            disabled={bulkRunning}
            data-testid="confirm-all-high-btn"
            className="w-full h-11 bg-primary text-black font-heading font-bold uppercase tracking-wider text-xs border-2 border-black shadow-[3px_3px_0_0_#111111] hover:shadow-[1px_1px_0_0_#111111] hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Confirm all high-confidence (≥90%) — {highConfUnreviewed.length} field{highConfUnreviewed.length !== 1 ? 's' : ''}
          </button>
        )}

        {/* Collapsed confirmed summary */}
        {collapseConfirmed && confirmedFields.length > 0 && (
          <button
            onClick={() => setShowConfirmed(!showConfirmed)}
            data-testid="confirmed-collapse-toggle"
            className="w-full flex items-center justify-between px-3 py-2 border-2 border-emerald-600 bg-emerald-50 text-emerald-800 text-xs font-heading font-bold uppercase tracking-wider"
          >
            <span>{confirmedFields.length} confirmed</span>
            {showConfirmed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}

        {/* Field rows */}
        <div className="space-y-2">
          {(collapseConfirmed && !showConfirmed ? attentionFields : fields).map((f) => (
            <ReviewableField
              key={f.field_id}
              field={f}
              state={stateOf(f)}
              locked={locked}
              onConfirm={handleConfirm}
              onCorrect={handleCorrect}
              onReject={handleReject}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldReviewPanel;
